import type { TinyAIPageContext } from '@repo/types';
import { kitResultsViewable } from '@repo/utils';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getHomePageTinyAccountId } from 'store/home';
import {
  getTinyAILatestKitForMainAccount,
  getTinyAILatestKitForTinyAccountId,
} from 'store/kits';
import { getImpersonateId } from 'store/user';
import { extractSubMetricIdFromPath } from './extractSubMetricIdFromPath';
import { useCurrentKitAndTinyAccount } from './useCurrentKitAndTinyAccount';
import { useTinyAIPageSource } from './useTinyAIPageSource';

export const useTinyAIPageContext = (): TinyAIPageContext => {
  const location = useLocation();
  const params = useParams<{
    subMetricId?: string;
    actionId?: string;
  }>();

  const { currentKit, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const pageSource = useTinyAIPageSource();
  const impersonateId = useSelector(getImpersonateId);

  const homePageTinyAccountId = useSelector(getHomePageTinyAccountId);
  const isHomeRoute = location.pathname === '/';
  const isHomeScopedToTinyAccount = isHomeRoute && !!homePageTinyAccountId;
  const homeFallbackKit = useSelector(
    getTinyAILatestKitForTinyAccountId(
      isHomeScopedToTinyAccount ? homePageTinyAccountId : null,
    ),
  );
  const mainAccountFallbackKit = useSelector(
    getTinyAILatestKitForMainAccount(),
  );

  const fallbackKit = isHomeScopedToTinyAccount
    ? homeFallbackKit
    : mainAccountFallbackKit;
  const resolvedKit = currentKit ?? fallbackKit ?? null;
  const resolvedKitId = currentKitId ?? resolvedKit?.id;
  const resolvedTinyAccountId =
    currentTinyAccountId ?? resolvedKit?.tinyaccount_id ?? null;

  return useMemo(() => {
    const pathname = location.pathname;

    const baseContext: TinyAIPageContext = {
      url: pathname,
      source: pageSource,
    };

    if (resolvedKitId) {
      baseContext.kitId = resolvedKitId;
    }

    if (resolvedTinyAccountId) {
      baseContext.tinyAccountId = resolvedTinyAccountId;
    }

    if (resolvedKit) {
      baseContext.resultsViewable = kitResultsViewable(
        resolvedKit,
        !!impersonateId,
      );
    }

    if (resolvedKit?.pregnant != null) {
      baseContext.isPregnant = Boolean(resolvedKit.pregnant);
    }

    if (resolvedKit?.practitioner_id) {
      baseContext.hasLinkedPractitioner = true;
    }

    // Prefer route params, then recover from pathname (nested layouts / odd
    // category slugs can miss params — AI-205 Akkermansia-style misses).
    if (params.subMetricId && /^\d+$/.test(params.subMetricId)) {
      baseContext.subMetricId = params.subMetricId;
    } else {
      const fromPath = extractSubMetricIdFromPath(pathname);
      if (fromPath) {
        baseContext.subMetricId = fromPath;
      }
    }

    const actionPlanMatch = pathname.match(/\/action\/(\d+)/);
    if (actionPlanMatch) {
      baseContext.actionPlanItemId = actionPlanMatch[1];
    }

    return baseContext;
  }, [
    location.pathname,
    params.subMetricId,
    params.actionId,
    resolvedKit,
    resolvedKitId,
    resolvedTinyAccountId,
    impersonateId,
    pageSource,
  ]);
};
